import React from 'react';
import { Formik, Form, FieldArray } from 'formik';

import { FormikFormGroup, FormikInput, Icon, ModalSubmit } from 'components/common';
import { Modal, Row, Col, Button, Alert } from 'components/bootstrap';

import useRepositories from './hooks/useRepositories';
import type { HadoopStorageRepository } from './types';

type Props = {
  hideModal: () => void;
};

type Pair = { id: string, key: string, value: string };
type ConfArrayType = Pair[];
type RepositoryFormValues = { confArray: ConfArrayType } & Pick<HadoopStorageRepository, 'name' | 'uri' | 'path' | 'load_defaults' | 'conf'>;

const HadoopStorageRepositoryModalForm = ({ hideModal }: Props) => {
  const { onCreateRepository } = useRepositories();

  const handleSubmit = (values: RepositoryFormValues) => {
    const conf = values.confArray.reduce((acc, { key, value }) => {
      if (key) acc[key] = value;

      return acc;
    }, {});

    const { confArray: _confArray, ...valuesWithoutConfArray } = values;
    const valuesWithConf = { ...valuesWithoutConfArray, conf };

    onCreateRepository({ type: 'hdfs', ...valuesWithConf }).then(() => hideModal());
  }

  const initialValues = {
    name: '',
    uri: '',
    path: '',
    load_defaults: true,
    conf: {},
    confArray: [],
  };

  return (
    <Formik<RepositoryFormValues> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ isSubmitting, isValidating, values }) => (
        <Form>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <FormikFormGroup
                  type="text"
                  label="Name"
                  name="name"
                  help="Name of your warm storage repository. Must be unique."
                  required
                />
                <FormikFormGroup
                  type="text"
                  label="Uri"
                  name="uri"
                  help="Uri of the nameserver"
                  required
                />
                <FormikFormGroup
                  type="text"
                  label="Path"
                  name="path"
                  help="Path of the warm storage repository"
                  required
                />
                <FormikFormGroup
                  label="Load defaults"
                  name="load_defaults"
                  help="Whether to load the default Hadoop configuration or not. (Enabled by default)"
                  type="checkbox"
                />
              </Col>
            </Row>
            <br />
            <Row>
              <Col md={12}>
                <h5><b>Configuration properties</b></h5>
                <Alert bsStyle="info">
                  <code>conf.{'<'}key{'>'}</code>
                  <p>Inlined configuration parameter to be added to Hadoop configuration. (Optional) Only client oriented properties from the hadoop core and hdfs configuration files will be recognized by the plugin.</p>
                </Alert>
                <FieldArray name="confArray">
                  {({ remove, push }) => (
                    <div>
                      {values.confArray.length > 0 &&
                        values.confArray.map((pair: Pair, index: number) => (
                          <Row key={pair.id}>
                            <Col md={5}>
                              <FormikInput
                                id={`${pair.key}-key`}
                                name={`confArray.${index}.key`}
                                placeholder="Key"
                                type="text"
                              />
                            </Col>
                            <Col md={5}>
                              <FormikInput
                                id={`${pair.key}-value`}
                                name={`confArray.${index}.value`}
                                placeholder="Value"
                                type="text"
                              />
                            </Col>
                            <Col md={1}>
                              <Button bsStyle="danger" onClick={() => remove(index)}>
                                <Icon name="delete" />
                              </Button>
                            </Col>
                          </Row>
                        ))}
                      <Button
                        bsStyle="primary"
                        type="button"
                        onClick={() => push({ id: `newRow-${new Date().getTime()}`, key: '', value: '' })}>
                        <Icon name="add" /> Add Conf property
                      </Button>
                    </div>
                  )}
                </FieldArray>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <ModalSubmit
              submitButtonText="Create"
              submitLoadingText="Creating..."
              onCancel={hideModal}
              disabledSubmit={isValidating}
              isSubmitting={isSubmitting}
            />
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  );
};

export default HadoopStorageRepositoryModalForm;
